import { SvgIconComponent } from "@mui/icons-material";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import NotesIcon from "@mui/icons-material/Notes";
import NumbersIcon from "@mui/icons-material/Numbers";
import ListIcon from "@mui/icons-material/List";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import MicIcon from "@mui/icons-material/Mic";
import PermMediaOutlinedIcon from "@mui/icons-material/PermMediaOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import FunctionsIcon from "@mui/icons-material/Functions";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DrawIcon from "@mui/icons-material/Draw";
import BrushIcon from "@mui/icons-material/Brush";
import LinkIcon from "@mui/icons-material/Link";
import { FieldWidget } from "../../types/fieldForm";

export interface PaletteField {
  widget: FieldWidget;
  label: string;
  icon: SvgIconComponent;
  description: string;
}

export interface PaletteGroup {
  label: string;
  /** Accent colour used for the left border and icon tint of fields in this group. */
  color: string;
  fields: PaletteField[];
}

/**
 * Palette restricted to the widgets the mobile renderer + backend `schema`
 * contract support (guide §4). Repeatable / Record Link remain excluded until
 * their contract lands (tracked as FB12 deferred). File Attachment, Signature and
 * Sketch are media-family widgets (captured as a UnifiedRelationship like `media`);
 * Hyperlink and Barcode are validated/plain text widgets.
 */
export const FIELD_GROUPS: PaletteGroup[] = [
  {
    label: "Basic",
    color: "#2563EB",
    fields: [
      { widget: "text", label: "Text", icon: TextFieldsIcon, description: "A single line of free text input." },
      { widget: "textarea", label: "Paragraph", icon: NotesIcon, description: "Multi-line text for longer responses." },
      { widget: "number", label: "Number", icon: NumbersIcon, description: "Numeric input that only accepts numbers." }
    ]
  },
  {
    label: "Choice",
    color: "#7C3AED",
    fields: [
      { widget: "select", label: "Single choice", icon: ListIcon, description: "Choose one option from a list." },
      { widget: "multiselect", label: "Multiple choice", icon: CheckBoxIcon, description: "Choose one or more options from a list." },
      {
        widget: "classification",
        label: "Classification",
        icon: AccountTreeOutlinedIcon,
        description: "Choose from a nested/hierarchical list of options."
      },
      { widget: "yesno", label: "Yes / No", icon: ToggleOnIcon, description: "A simple boolean toggle between yes and no." }
    ]
  },
  {
    label: "Date & time",
    color: "#0D9488",
    fields: [
      { widget: "date", label: "Date", icon: CalendarMonthIcon, description: "Pick a calendar date." },
      { widget: "datetime", label: "Date & time", icon: EventIcon, description: "Pick a date and time." },
      { widget: "time", label: "Time", icon: ScheduleIcon, description: "Pick a time of day." }
    ]
  },
  {
    label: "Media",
    color: "#DB2777",
    fields: [
      {
        widget: "media",
        label: "Media",
        icon: PermMediaOutlinedIcon,
        description: "Capture photos, video, or audio. Stored as attachments on the record."
      },
      {
        widget: "attachment",
        label: "File attachment",
        icon: AttachFileIcon,
        description: "Attach one or more files. Stored as attachments on the record."
      },
      {
        widget: "signature",
        label: "Signature",
        icon: DrawIcon,
        description: "Capture an on-screen signature. Saved as an image on the record."
      },
      {
        widget: "sketch",
        label: "Sketch",
        icon: BrushIcon,
        description: "Draw a free-hand sketch. Saved as an image on the record."
      }
    ]
  },
  {
    label: "Advanced",
    color: "#B45309",
    fields: [
      { widget: "address", label: "Address", icon: PlaceOutlinedIcon, description: "Capture a postal / street address." },
      { widget: "barcode", label: "Barcode", icon: QrCode2Icon, description: "Scan or enter a barcode / QR value." },
      { widget: "hyperlink", label: "Hyperlink", icon: LinkIcon, description: "A text field validated as a URL / web link." },
      {
        widget: "calculated",
        label: "Calculation",
        icon: FunctionsIcon,
        description: "A read-only value computed automatically from an expression."
      }
    ]
  },
  {
    label: "Layout",
    color: "#7C3AED",
    fields: [
      {
        widget: "section",
        label: "Section",
        icon: ViewAgendaOutlinedIcon,
        description: "A titled group that holds the fields placed under it."
      },
      { widget: "label", label: "Label", icon: LabelOutlinedIcon, description: "Static instructions or a note (captures no data)." }
    ]
  }
];

const ICON_BY_WIDGET: Partial<Record<FieldWidget, SvgIconComponent>> = {
  text: TextFieldsIcon,
  textarea: NotesIcon,
  number: NumbersIcon,
  select: ListIcon,
  multiselect: CheckBoxIcon,
  classification: AccountTreeOutlinedIcon,
  yesno: ToggleOnIcon,
  checkbox: CheckBoxIcon,
  date: CalendarMonthIcon,
  datetime: EventIcon,
  time: ScheduleIcon,
  address: PlaceOutlinedIcon,
  barcode: QrCode2Icon,
  hyperlink: LinkIcon,
  calculated: FunctionsIcon,
  media: PermMediaOutlinedIcon,
  attachment: AttachFileIcon,
  signature: DrawIcon,
  sketch: BrushIcon,
  photo: ImageIcon,
  video: VideocamIcon,
  audio: MicIcon,
  section: ViewAgendaOutlinedIcon,
  label: LabelOutlinedIcon
};

const COLOR_BY_WIDGET: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of FIELD_GROUPS) {
    for (const field of group.fields) {
      if (!(field.widget in map)) map[field.widget] = group.color;
    }
  }
  return map;
})();

const LABEL_BY_WIDGET: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of FIELD_GROUPS) {
    for (const field of group.fields) {
      if (!(field.widget in map)) map[field.widget] = field.label;
    }
  }
  return map;
})();

/** Returns the icon component for a given widget. */
export function iconForWidget(widget: FieldWidget): SvgIconComponent {
  return ICON_BY_WIDGET[widget] ?? TextFieldsIcon;
}

/** Returns the category accent colour for a given widget. */
export function colorForWidget(widget: FieldWidget): string {
  return COLOR_BY_WIDGET[widget] ?? "#2563EB";
}

/** Returns the default display label used when adding a widget. */
export function defaultLabelForWidget(widget: FieldWidget): string {
  return LABEL_BY_WIDGET[widget] ?? "Field";
}

/**
 * Media-family widgets — captured as a UnifiedRelationship on the row (no
 * `row_data` column, `target:null`). Covers flexible media plus file attachment,
 * signature and sketch (both saved as images). Capture is handled by the mobile app.
 */
const MEDIA_WIDGETS = new Set<FieldWidget>(["media", "photo", "video", "audio", "attachment", "signature", "sketch"]);

/** True when the widget captures media/files via a relationship (no data column). */
export function isMediaWidget(widget: FieldWidget): boolean {
  return MEDIA_WIDGETS.has(widget);
}
